// File: src/state/useOmniCineTimeline.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { timelineReducer, DEFAULT_TIMELINE_STATE } from "@/state/timeline/timeline.reducer";
import type { TimelineAction, TimelineActionMeta, TimelineAnnotation, TimelineState } from "@/state/timeline/timeline.types";
import type { TimelineCapsule } from "@/components/OmniCine/Timeline/Lanes/capsules/capsule.types";
import type { LaneDefinition, LaneItem } from "@/components/OmniCine/Timeline/Lanes/lane.types";
import type { LaneDependencyRelation, LaneSyncBadge } from "@/components/OmniCine/Timeline/Lanes/sync/sync.types";
import type {
  AuditionMode,
  ClipMediaMetadata,
  EditorialClipBinding,
  MediaFamily,
  MediaMember,
  MediaUnionClipLike,
} from "@/components/OmniCine/Timeline/MediaUnion";
import {
  deriveMediaRegistryFromTimeline,
  reconcileEditorialClipBindings,
} from "@/components/OmniCine/Timeline/MediaUnion";
import type { NarrativeSonicsReadModel } from "@/components/OmniCine/Timeline/NarrativeSonics.types";
import { buildExplicitEditorialClipBinding } from "@/state/editorial/editorial.commands";
import { deriveNarrativeSonicsReadModel } from "@/state/sonics/sonics.readModel";
import type { EmotionalIntentSegment, FxIntentNode, AuthoredSonicsState } from "@/state/sonics/sonics.types";
import { DEFAULT_AUTHORED_SONICS_STATE, authoredSonicsReducer } from "@/state/sonics/sonics.reducer";
import { DEFAULT_TIMELINE_LANE_STATE, timelineLanesReducer } from "@/state/timeline/timeline.lanes.reducer";
import type { TimelineLaneState } from "@/state/timeline/timeline.lanes.types";

export type {
  TimelineAction,
  TimelineClip,
  TimelineMarker,
  TimelineRange,
  TimelineSnap,
  TimelineState,
  TimelineTrack,
} from "@/state/timeline/timeline.types";
export type Track = import("@/state/timeline/timeline.types").TimelineTrack;

const CLIP_MEDIA_METADATA_EVENT = "omnicine:clip:media:metadata";

export type OmniCineTimelineCtx = {
  state: TimelineState;
  dispatch: (action: TimelineAction) => void;
  addAnnotation: (annotation: TimelineAnnotation, meta?: TimelineActionMeta) => void;
  updateAnnotation: (annotationId: string, patch: Partial<TimelineAnnotation>, meta?: TimelineActionMeta) => void;
  lanes: {
    state: TimelineLaneState;
    registerLane: (lane: LaneDefinition) => void;
    registerLanes: (lanes: LaneDefinition[]) => void;
    updateLane: (laneId: string, patch: Partial<LaneDefinition>) => void;
    setLaneItems: (laneId: string, items: LaneItem[]) => void;
    upsertCapsule: (capsule: TimelineCapsule) => void;
    setDependencyRelations: (relations: LaneDependencyRelation[]) => void;
    setSyncBadges: (badges: LaneSyncBadge[]) => void;
  };
  sonics: {
    state: AuthoredSonicsState;
    authorEmotionalIntentSegment: (segment: EmotionalIntentSegment) => { operation: "added" | "updated"; segment: EmotionalIntentSegment };
    discardEmotionalIntentSegment: (segmentId: string) => { removed: boolean; segmentId: string };
    authorFxIntentNode: (node: FxIntentNode) => { operation: "added" | "updated"; node: FxIntentNode };
    discardFxIntentNode: (nodeId: string) => { removed: boolean; nodeId: string };
  };

  // ✅ UI-only selection (NOT reducer-owned)
  selectedClipId: string | null;
  auditionMode: AuditionMode;
  setAuditionMode: (mode: AuditionMode) => void;
  mediaFamilies: MediaFamily[];
  mediaMembers: MediaMember[];
  editorialClipBindings: EditorialClipBinding[];
  editorialClipBindingByClipId: Record<string, EditorialClipBinding>;
  bindEditorialClip: (args: { clipId: string; familyId: string; audioMemberId?: string; videoMemberId?: string }) => void;
  clearEditorialClipBinding: (clipId: string) => void;
  clipMediaMetadataByClipId: Record<string, ClipMediaMetadata>;
  recordClipMediaMetadata: (metadata: ClipMediaMetadata) => void;
  narrativeSonicsReadModel: NarrativeSonicsReadModel | null;

  // ✅ legacy-friendly shape (optional but helpful)
  ui: { selectionIds: string[] };
};

const timelineContextHost = globalThis as typeof globalThis & {
  __OMNICINE_TIMELINE_CONTEXT__?: React.Context<OmniCineTimelineCtx | null>;
};

// Reuse the same context object across Vite HMR updates so providers and
// consumers do not drift onto different module instances mid-refresh.
const OmniCineTimelineContext =
  timelineContextHost.__OMNICINE_TIMELINE_CONTEXT__ ?? createContext<OmniCineTimelineCtx | null>(null);

timelineContextHost.__OMNICINE_TIMELINE_CONTEXT__ = OmniCineTimelineContext;

function readClipId(clip: unknown): string | null {
  const candidate = clip as { id?: string; clipId?: string } | null | undefined;
  const clipId = String(candidate?.id ?? candidate?.clipId ?? "").trim();
  return clipId || null;
}

function finiteNum(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function makeDebugId(prefix: string): string {
  try {
    const cryptoApi = globalThis.crypto as Crypto | undefined;
    if (cryptoApi?.randomUUID) return `${prefix}-${cryptoApi.randomUUID()}`;
  } catch {
    // ignore
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function collectTimelineClipIds(state: TimelineState): Set<string> {
  const clipIds = new Set<string>();
  const candidates: unknown[] = [];

  if (Array.isArray((state as any)?.clips)) {
    candidates.push(...((state as any).clips as unknown[]));
  }

  if (Array.isArray((state as any)?.tracks)) {
    for (const track of (state as any).tracks as Array<{ clips?: unknown[] }>) {
      if (Array.isArray(track?.clips)) candidates.push(...track.clips);
    }
  }

  for (const candidate of candidates) {
    const clipId = readClipId(candidate);
    if (clipId) clipIds.add(clipId);
  }

  return clipIds;
}

function collectTimelineClips(state: TimelineState): MediaUnionClipLike[] {
  const collected: MediaUnionClipLike[] = [];
  const seen = new Set<string>();

  const pushClip = (clip: unknown) => {
    const clipId = readClipId(clip);
    if (!clipId || seen.has(clipId)) return;
    seen.add(clipId);
    collected.push(clip as MediaUnionClipLike);
  };

  if (Array.isArray((state as any)?.clips)) {
    for (const clip of (state as any).clips as unknown[]) pushClip(clip);
  }

  if (Array.isArray((state as any)?.tracks)) {
    for (const track of (state as any).tracks as Array<{ clips?: unknown[] }>) {
      if (!Array.isArray(track?.clips)) continue;
      for (const clip of track.clips) pushClip(clip);
    }
  }

  return collected;
}

function mergeDefinedRecord(
  previousValue?: Record<string, unknown>,
  nextValue?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!previousValue && !nextValue) return undefined;
  return {
    ...(previousValue ?? {}),
    ...(nextValue ?? {}),
  };
}

function mergeListByKey<TItem>(
  previousList: TItem[] | undefined,
  nextList: TItem[] | undefined,
  keyFor: (item: TItem, index: number) => string,
): TItem[] | undefined {
  if (!nextList) return previousList;
  if (!previousList?.length) return nextList;

  const merged = new Map<string, TItem>();
  for (const [index, item] of previousList.entries()) {
    merged.set(keyFor(item, index), item);
  }
  for (const [index, item] of nextList.entries()) {
    merged.set(keyFor(item, index), item);
  }

  return Array.from(merged.values());
}

export function OmniCineTimelineProvider({ children }: { children: React.ReactNode }) {
  const [state, baseDispatch] = useReducer(timelineReducer, DEFAULT_TIMELINE_STATE);
  const [timelineLaneState, setTimelineLaneState] = useState<TimelineLaneState>(DEFAULT_TIMELINE_LANE_STATE);
  const [authoredSonicsState, setAuthoredSonicsState] = useState<AuthoredSonicsState>(DEFAULT_AUTHORED_SONICS_STATE);

  // ✅ Live ref to latest reducer state (prevents stale closures in bridges)
  const stateRef = useRef<TimelineState>(state);
  const authoredSonicsStateRef = useRef<AuthoredSonicsState>(authoredSonicsState);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  useEffect(() => {
    authoredSonicsStateRef.current = authoredSonicsState;
  }, [authoredSonicsState]);

  // UI-only selection
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [auditionModeState, setAuditionModeState] = useState<AuditionMode>("clip");
  const [mediaFamilies, setMediaFamilies] = useState<MediaFamily[]>([]);
  const [mediaMembers, setMediaMembers] = useState<MediaMember[]>([]);
  const [editorialClipBindings, setEditorialClipBindings] = useState<EditorialClipBinding[]>([]);
  const [clipMediaMetadataByClipId, setClipMediaMetadataByClipId] = useState<Record<string, ClipMediaMetadata>>({});

  const setAuditionMode = useCallback((mode: AuditionMode) => {
    setAuditionModeState(mode);
  }, []);

  const bindEditorialClip = useCallback((args: {
    clipId: string;
    familyId: string;
    audioMemberId?: string;
    videoMemberId?: string;
  }) => {
    const nextBinding = buildExplicitEditorialClipBinding({
      clipId: args.clipId,
      familyId: args.familyId,
      mediaFamilies,
      mediaMembers,
      audioMemberId: args.audioMemberId,
      videoMemberId: args.videoMemberId,
      existingBinding: editorialClipBindings.find((entry) => entry.clipId === args.clipId) ?? null,
    });
    if (!nextBinding) return;

    setEditorialClipBindings((prev) => {
      const rest = prev.filter((entry) => entry.clipId !== nextBinding.clipId);
      return [...rest, nextBinding];
    });
  }, [editorialClipBindings, mediaFamilies, mediaMembers]);

  const clearEditorialClipBinding = useCallback((clipId: string) => {
    const resolvedClipId = String(clipId ?? "").trim();
    if (!resolvedClipId) return;
    setEditorialClipBindings((prev) => prev.filter((entry) => entry.clipId !== resolvedClipId));
  }, []);

  const recordClipMediaMetadata = useCallback((metadata: ClipMediaMetadata) => {
    const clipId = String(metadata.clipId ?? "").trim();
    if (!clipId) return;

    const durationSec = Number(metadata.durationSec ?? NaN);
    const mediaUrl = String(metadata.mediaUrl ?? "").trim() || undefined;
    const kind = metadata.kind;

    setClipMediaMetadataByClipId((prev) => {
      const previousEntry = prev[clipId];
      const nextEntry: ClipMediaMetadata = {
        ...previousEntry,
        ...metadata,
        clipId,
        durationSec: Number.isFinite(durationSec) && durationSec > 0 ? durationSec : previousEntry?.durationSec,
        mediaUrl: mediaUrl ?? previousEntry?.mediaUrl,
        kind: kind ?? previousEntry?.kind,
        proxies: mergeListByKey(previousEntry?.proxies, metadata.proxies, (item, index) => String(item?.id ?? `${item?.kind ?? "proxy"}:${item?.tier ?? "tier"}:${index}`)),
        metadataHistory: mergeListByKey(previousEntry?.metadataHistory, metadata.metadataHistory, (item, index) => String(item?.id ?? `${item?.layer ?? "layer"}:${item?.at ?? 0}:${index}`)),
        transcodeHistory: mergeListByKey(previousEntry?.transcodeHistory, metadata.transcodeHistory, (item, index) => String(item?.id ?? `${item?.tool ?? "tool"}:${item?.at ?? 0}:${index}`)),
        metadata: mergeDefinedRecord(previousEntry?.metadata, metadata.metadata),
        metadataVault: mergeDefinedRecord(previousEntry?.metadataVault, metadata.metadataVault),
        sourceMetadata: mergeDefinedRecord(previousEntry?.sourceMetadata, metadata.sourceMetadata),
        semanticMetadata: mergeDefinedRecord(previousEntry?.semanticMetadata, metadata.semanticMetadata),
        userMetadata: mergeDefinedRecord(previousEntry?.userMetadata, metadata.userMetadata),
        systemMetadata: mergeDefinedRecord(previousEntry?.systemMetadata, metadata.systemMetadata),
      };

      if (JSON.stringify(previousEntry ?? null) === JSON.stringify(nextEntry)) {
        return prev;
      }

      return { ...prev, [clipId]: nextEntry };
    });
  }, []);

  const dispatch = useCallback(
    (action: TimelineAction) => {
      // --- UI-only selection tap (optional)
      if (action.type === "clip/select") {
        const cid = (action as any)?.payload?.clipId ?? null;
        setSelectedClipId(cid ? String(cid) : null);
      }

      // --- Always stamp meta.ts at dispatch boundary
      const a: TimelineAction = {
        ...(action as any),
        meta: {
          ...(action as any)?.meta,
          ts: typeof (action as any)?.meta?.ts === "number" ? (action as any).meta.ts : Date.now(),
        },
      };

      baseDispatch(a);
    },
    [baseDispatch],
  );

  const addAnnotation = useCallback(
    (annotation: TimelineAnnotation, meta?: TimelineActionMeta) => {
      dispatch({
        type: "annotation/add",
        payload: { annotation },
        meta,
      });
    },
    [dispatch],
  );

  const updateAnnotation = useCallback(
    (annotationId: string, patch: Partial<TimelineAnnotation>, meta?: TimelineActionMeta) => {
      const resolvedId = String(annotationId ?? "").trim();
      if (!resolvedId) return;

      dispatch({
        type: "annotation/update",
        payload: {
          annotationId: resolvedId,
          patch,
        },
        meta,
      });
    },
    [dispatch],
  );

  const registerLane = useCallback((lane: LaneDefinition) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/register", lane }),
    );
  }, []);

  const registerLanes = useCallback((lanes: LaneDefinition[]) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/registerMany", lanes }),
    );
  }, []);

  const updateLane = useCallback((laneId: string, patch: Partial<LaneDefinition>) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/update", laneId, patch }),
    );
  }, []);

  const setLaneItems = useCallback((laneId: string, items: LaneItem[]) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/setItems", laneId, items }),
    );
  }, []);

  const upsertCapsule = useCallback((capsule: TimelineCapsule) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/upsertCapsule", capsule }),
    );
  }, []);

  const setDependencyRelations = useCallback((relations: LaneDependencyRelation[]) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/setDependencyRelations", relations }),
    );
  }, []);

  const setSyncBadges = useCallback((badges: LaneSyncBadge[]) => {
    setTimelineLaneState((prev) =>
      timelineLanesReducer(prev, { type: "timeline/lanes/setSyncBadges", badges }),
    );
  }, []);

  const upsertEmotionalIntentSegment = useCallback((segment: EmotionalIntentSegment) => {
    setAuthoredSonicsState((prev) =>
      authoredSonicsReducer(prev, { type: "sonics/emotion/upsert", segment }),
    );
  }, []);

  const authorEmotionalIntentSegment = useCallback((segment: EmotionalIntentSegment) => {
    let operation: "added" | "updated" = "added";

    setAuthoredSonicsState((prev) => {
      operation = prev.emotionalIntentSegmentsById[segment.id] ? "updated" : "added";
      return authoredSonicsReducer(prev, { type: "sonics/emotion/upsert", segment });
    });

    return { operation, segment };
  }, []);

  const discardEmotionalIntentSegment = useCallback((segmentId: string) => {
    const resolvedSegmentId = String(segmentId ?? "").trim();
    if (!resolvedSegmentId) return { removed: false, segmentId: resolvedSegmentId };

    let removed = false;

    setAuthoredSonicsState((prev) => {
      removed = !!prev.emotionalIntentSegmentsById[resolvedSegmentId];
      return authoredSonicsReducer(prev, { type: "sonics/emotion/remove", segmentId: resolvedSegmentId });
    });

    return { removed, segmentId: resolvedSegmentId };
  }, []);

  const upsertFxIntentNode = useCallback((node: FxIntentNode) => {
    setAuthoredSonicsState((prev) =>
      authoredSonicsReducer(prev, { type: "sonics/fx/upsert", node }),
    );
  }, []);

  const authorFxIntentNode = useCallback((node: FxIntentNode) => {
    let operation: "added" | "updated" = "added";

    setAuthoredSonicsState((prev) => {
      operation = prev.fxIntentNodesById[node.id] ? "updated" : "added";
      return authoredSonicsReducer(prev, { type: "sonics/fx/upsert", node });
    });

    return { operation, node };
  }, []);

  const discardFxIntentNode = useCallback((nodeId: string) => {
    const resolvedNodeId = String(nodeId ?? "").trim();
    if (!resolvedNodeId) return { removed: false, nodeId: resolvedNodeId };

    let removed = false;

    setAuthoredSonicsState((prev) => {
      removed = !!prev.fxIntentNodesById[resolvedNodeId];
      return authoredSonicsReducer(prev, { type: "sonics/fx/remove", nodeId: resolvedNodeId });
    });

    return { removed, nodeId: resolvedNodeId };
  }, []);

  useEffect(() => {
    const validClipIds = collectTimelineClipIds(state);
    const timelineClips = collectTimelineClips(state);
    const registry = deriveMediaRegistryFromTimeline(timelineClips, clipMediaMetadataByClipId);

    setMediaFamilies(registry.families);
    setMediaMembers(registry.members);

    setEditorialClipBindings((prev) =>
      reconcileEditorialClipBindings({
        clips: timelineClips,
        mediaFamilies: registry.families,
        mediaMembers: registry.members,
        existingBindings: prev,
      }),
    );

    setClipMediaMetadataByClipId((prev) => {
      const nextEntries = Object.entries(prev).filter(([clipId]) => validClipIds.has(clipId));
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [clipMediaMetadataByClipId, state]);

  useEffect(() => {
    const onClipMediaMetadata: EventListener = (event) => {
      recordClipMediaMetadata(((event as CustomEvent).detail ?? {}) as ClipMediaMetadata);
    };

    window.addEventListener(CLIP_MEDIA_METADATA_EVENT, onClipMediaMetadata);
    return () => window.removeEventListener(CLIP_MEDIA_METADATA_EVENT, onClipMediaMetadata);
  }, [recordClipMediaMetadata]);

  // ✅ DEV-only state taps (read-only): robust clip locator across common shapes
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // StrictMode runs effects twice in DEV, so we install hooks only once.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    if (w.__oc_debug_hooks_installed_v1) return;
    w.__oc_debug_hooks_installed_v1 = true;

    function asArray(x: any): any[] | null {
      return Array.isArray(x) ? x : null;
    }

    function pickFirstClipsContainer(st: any): any[] {
      if (!st) return [];

      // Prefer clips attached to tracks (runtime often uses tracks[].clips).
      const tracks = asArray(st.tracks);
      if (tracks) {
        const all = tracks.flatMap((t: any) => asArray(t?.clips) ?? []);
        if (all.length) return all;
      }

      // Fall back to other shapes (state.clips is canonical but sometimes empty at runtime).
      const direct = asArray(st.clips);
      if (direct) return direct;

      const tl = asArray(st.timeline?.clips);
      if (tl) return tl;

      const present = asArray(st.present?.timeline?.clips) ?? asArray(st.present?.clips);
      if (present) return present;

      const doc = asArray(st.doc?.timeline?.clips) ?? asArray(st.doc?.clips);
      if (doc) return doc;

      // Map/dict shape
      const byId = st.clipsById;
      if (byId && typeof byId === "object") {
        const vals = Object.values(byId);
        if (vals.length) return vals as any[];
      }

      return [];
    }

    w.__oc_debug_state = () => stateRef.current;

    w.__oc_debug_clips = () => {
      const st: any = stateRef.current;
      const out = pickFirstClipsContainer(st);
      return Array.isArray(out) ? out : [];
    };

    // DEV: wire global debug bridge `__oc_debug` to the reducer-backed timeline
    // so that external dispatches target the same store the UI reads from.
    // This avoids mixed-store dispatch/read problems during development.
    w.__oc_debug = w.__oc_debug || {};
    w.__oc_debug.getState = () => stateRef.current;
    // Expose the reducer dispatch (wrapped) so tests and debug console drive
    // the same pipeline that React uses.
    w.__oc_debug.dispatch = (action: any) => dispatch(action as any);
    // DEV helper: expose the raw reducer dispatch for sync-driven console tests
    // (useful when you need the reducer to run immediately from the console).
    // Note: this bypasses provider-side UI-only taps (e.g. clip/select handling).
    // Use only in DEV diagnostics.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (w.__oc_debug as any).dispatchImmediate = (action: any) => baseDispatch(action as any);
    // Also create a short alias to reduce typo-prone console calls (`oc_debug`).
    w.oc_debug = w.__oc_debug;
    w.__oc_debug.__attached = (w.__oc_debug.__attached || "") + "|OmniCineTimelineProvider";

    // Helpful: prints where clips were found
    w.__oc_debug_clips_where = () => {
      const st: any = stateRef.current;

      const paths: Array<[string, any]> = [
        ["clips", st?.clips],
        ["timeline.clips", st?.timeline?.clips],
        ["present.timeline.clips", st?.present?.timeline?.clips],
        ["present.clips", st?.present?.clips],
        ["doc.timeline.clips", st?.doc?.timeline?.clips],
        ["doc.clips", st?.doc?.clips],
        ["tracks[].clips", st?.tracks?.map?.((t: any) => t?.clips)],
        ["clipsById", st?.clipsById ? Object.values(st.clipsById) : null],
      ];

      const found = paths
        .map(([k, v]) => {
          if (Array.isArray(v)) return [k, v.length] as const;
          if (Array.isArray(v?.[0])) return [k, v.flat().length] as const; // tracks[].clips (array-of-arrays)
          return [k, 0] as const;
        })
        .filter(([, n]) => n > 0);

      return { found, keys: Object.keys(st || {}) };
    };
  }, []);

  const editorialClipBindingByClipId = useMemo<Record<string, EditorialClipBinding>>(
    () => Object.fromEntries(editorialClipBindings.map((binding) => [binding.clipId, binding])),
    [editorialClipBindings],
  );

  const lanes = useMemo<OmniCineTimelineCtx["lanes"]>(
    () => ({
      state: timelineLaneState,
      registerLane,
      registerLanes,
      updateLane,
      setLaneItems,
      upsertCapsule,
      setDependencyRelations,
      setSyncBadges,
    }),
    [
      registerLane,
      registerLanes,
      setDependencyRelations,
      setLaneItems,
      setSyncBadges,
      timelineLaneState,
      updateLane,
      upsertCapsule,
    ],
  );

  const sonics = useMemo<OmniCineTimelineCtx["sonics"]>(
    () => ({
      state: authoredSonicsState,
      authorEmotionalIntentSegment,
      discardEmotionalIntentSegment,
      authorFxIntentNode,
      discardFxIntentNode,
    }),
    [
      authorEmotionalIntentSegment,
      authorFxIntentNode,
      authoredSonicsState,
      discardEmotionalIntentSegment,
      discardFxIntentNode,
    ],
  );

  const narrativeSonicsReadModel = useMemo<OmniCineTimelineCtx["narrativeSonicsReadModel"]>(
    () => deriveNarrativeSonicsReadModel({
      clips: collectTimelineClips(state),
      playheadSec: Number(state.playheadSec ?? 0),
      mediaFamilies,
      mediaMembers,
      editorialClipBindings,
      clipMediaMetadataByClipId,
      selectedClipId,
      auditionMode: auditionModeState,
      authoredSonicsState,
    }),
    [
      auditionModeState,
      authoredSonicsState,
      clipMediaMetadataByClipId,
      editorialClipBindings,
      mediaFamilies,
      mediaMembers,
      selectedClipId,
      state,
    ],
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const debugWindow = window as typeof window & {
      __oc_debug_sonics?: () => unknown;
    };

    const w = window as typeof window & {
      __oc_debug?: Record<string, unknown>;
    };

    w.__oc_debug = w.__oc_debug || {};

    const buildDebugFxNode = (input?: Partial<FxIntentNode> & { startSec?: number; endSec?: number; strength?: number; type?: FxIntentNode["type"] }) => {
      const playback = stateRef.current as any;
      const clips = collectTimelineClips(stateRef.current);
      const resolvedSelectedClipId = String(playback?.selectedClipId ?? selectedClipId ?? "").trim();
      const selectedClip = resolvedSelectedClipId
        ? clips.find((clip: any) => String(clip?.id ?? clip?.clipId ?? "").trim() === resolvedSelectedClipId) ?? null
        : null;
      const fallbackStartSec = finiteNum(playback?.playheadSec) ? Number(playback.playheadSec) : 0;
      const clipStartSec = finiteNum((selectedClip as any)?.startSec) ? Number((selectedClip as any).startSec) : fallbackStartSec;
      const clipEndSec = finiteNum((selectedClip as any)?.endSec) ? Number((selectedClip as any).endSec) : clipStartSec + 1.2;
      const startSec = Math.max(0, finiteNum(input?.startSec) ? Number(input?.startSec) : clipStartSec);
      const endSec = Math.max(startSec + 0.18, finiteNum(input?.endSec) ? Number(input?.endSec) : Math.min(clipEndSec, startSec + 0.72));

      return {
        id: String(input?.id ?? makeDebugId("fx-debug")),
        type: input?.type ?? "swell",
        startSec,
        endSec,
        strength: Math.max(0.12, Math.min(1, finiteNum(input?.strength) ? Number(input?.strength) : 0.74)),
        rationale: typeof input?.rationale === "string" && input.rationale.trim() ? input.rationale.trim() : "debug seeded pulse",
        authoredBy: "authored" as const,
        focusScope: input?.focusScope ?? "clip",
        focusClipId: input?.focusClipId ?? (resolvedSelectedClipId || undefined),
        focusSourceId: input?.focusSourceId ?? (String((selectedClip as any)?.sourceAssetId ?? (selectedClip as any)?.mediaId ?? (selectedClip as any)?.sourceId ?? "").trim() || undefined),
      } satisfies FxIntentNode;
    };

    const buildDebugArcSegment = (input?: Partial<EmotionalIntentSegment> & { startSec?: number; endSec?: number }) => {
      const playback = stateRef.current as any;
      const clips = collectTimelineClips(stateRef.current);
      const resolvedSelectedClipId = String(playback?.selectedClipId ?? selectedClipId ?? "").trim();
      const selectedClip = resolvedSelectedClipId
        ? clips.find((clip: any) => String(clip?.id ?? clip?.clipId ?? "").trim() === resolvedSelectedClipId) ?? null
        : null;
      const fallbackStartSec = finiteNum(playback?.playheadSec) ? Number(playback.playheadSec) : 0;
      const clipStartSec = finiteNum((selectedClip as any)?.startSec) ? Number((selectedClip as any).startSec) : fallbackStartSec;
      const clipEndSec = finiteNum((selectedClip as any)?.endSec) ? Number((selectedClip as any).endSec) : clipStartSec + 3.2;
      const startSec = Math.max(0, finiteNum(input?.startSec) ? Number(input?.startSec) : clipStartSec);
      const endSec = Math.max(startSec + 0.25, finiteNum(input?.endSec) ? Number(input?.endSec) : Math.min(clipEndSec, startSec + 1.8));

      return {
        id: String(input?.id ?? makeDebugId("arc-debug")),
        startSec,
        endSec,
        valence: Math.max(-1, Math.min(1, finiteNum(input?.valence) ? Number(input?.valence) : 0.22)),
        intensity: Math.max(0, Math.min(1, finiteNum(input?.intensity) ? Number(input?.intensity) : 0.68)),
        urgency: Math.max(0, Math.min(1, finiteNum(input?.urgency) ? Number(input?.urgency) : 0.44)),
        transitionSteepness: Math.max(0, Math.min(1, finiteNum(input?.transitionSteepness) ? Number(input?.transitionSteepness) : 0.56)),
        authoredBy: "authored" as const,
        focusScope: input?.focusScope ?? "clip",
        focusClipId: input?.focusClipId ?? (resolvedSelectedClipId || undefined),
        focusSourceId: input?.focusSourceId ?? (String((selectedClip as any)?.sourceAssetId ?? (selectedClip as any)?.mediaId ?? (selectedClip as any)?.sourceId ?? "").trim() || undefined),
      } satisfies EmotionalIntentSegment;
    };

    debugWindow.__oc_debug_sonics = () => authoredSonicsStateRef.current;
    w.__oc_debug.getEmotionalIntentSegments = () => Object.values(authoredSonicsStateRef.current.emotionalIntentSegmentsById);
    w.__oc_debug.getFxIntentNodes = () => Object.values(authoredSonicsStateRef.current.fxIntentNodesById);
    w.__oc_debug.upsertEmotionalIntentSegment = (input?: Partial<EmotionalIntentSegment>) => {
      const segment = buildDebugArcSegment(input);
      return authorEmotionalIntentSegment(segment);
    };
    w.__oc_debug.upsertFxIntentNode = (input?: Partial<FxIntentNode>) => {
      const node = buildDebugFxNode(input);
      return authorFxIntentNode(node);
    };
    w.__oc_debug.seedProjectionEmotionDemo = () => {
      const existing = Object.values(authoredSonicsStateRef.current.emotionalIntentSegmentsById);
      if (existing.length > 0) return existing;

      const seeded = [
        buildDebugArcSegment({ valence: 0.36, intensity: 0.62, urgency: 0.28 }),
        buildDebugArcSegment({ startSec: (finiteNum((stateRef.current as any)?.playheadSec) ? Number((stateRef.current as any).playheadSec) : 0) + 1.2, endSec: (finiteNum((stateRef.current as any)?.playheadSec) ? Number((stateRef.current as any).playheadSec) : 0) + 2.6, valence: -0.18, intensity: 0.82, urgency: 0.74 }),
      ];

      for (const segment of seeded) {
        authorEmotionalIntentSegment(segment);
      }
      return seeded;
    };
    w.__oc_debug.seedProjectionHapticDemo = () => {
      const existing = Object.values(authoredSonicsStateRef.current.fxIntentNodesById);
      if (existing.length > 0) return existing;

      const seeded = [
        buildDebugFxNode({ type: "swell", strength: 0.76 }),
        buildDebugFxNode({ type: "duck", startSec: (finiteNum((stateRef.current as any)?.playheadSec) ? Number((stateRef.current as any).playheadSec) : 0) + 0.82, endSec: (finiteNum((stateRef.current as any)?.playheadSec) ? Number((stateRef.current as any).playheadSec) : 0) + 1.38, strength: 0.58 }),
      ];

      for (const node of seeded) {
        authorFxIntentNode(node);
      }
      return seeded;
    };
    w.__oc_debug.clearEmotionalIntentSegments = () => {
      for (const segment of Object.values(authoredSonicsStateRef.current.emotionalIntentSegmentsById)) {
        discardEmotionalIntentSegment(segment.id);
      }
      return [];
    };
    w.__oc_debug.clearFxIntentNodes = () => {
      for (const node of Object.values(authoredSonicsStateRef.current.fxIntentNodesById)) {
        discardFxIntentNode(node.id);
      }
      return [];
    };

    return () => {
      delete w.__oc_debug?.getEmotionalIntentSegments;
      delete w.__oc_debug?.getFxIntentNodes;
      delete w.__oc_debug?.upsertEmotionalIntentSegment;
      delete w.__oc_debug?.upsertFxIntentNode;
      delete w.__oc_debug?.seedProjectionEmotionDemo;
      delete w.__oc_debug?.seedProjectionHapticDemo;
      delete w.__oc_debug?.clearEmotionalIntentSegments;
      delete w.__oc_debug?.clearFxIntentNodes;
      delete debugWindow.__oc_debug_sonics;
    };
  }, [
    authorEmotionalIntentSegment,
    authorFxIntentNode,
    authoredSonicsState,
    discardEmotionalIntentSegment,
    discardFxIntentNode,
    selectedClipId,
  ]);

  const value = useMemo<OmniCineTimelineCtx>(
    () => ({
      state,
      dispatch,
      addAnnotation,
      updateAnnotation,
      lanes,
      sonics,
      selectedClipId,
      auditionMode: auditionModeState,
      setAuditionMode,
      mediaFamilies,
      mediaMembers,
      editorialClipBindings,
      editorialClipBindingByClipId,
      bindEditorialClip,
      clearEditorialClipBinding,
      clipMediaMetadataByClipId,
      recordClipMediaMetadata,
      narrativeSonicsReadModel,
      ui: { selectionIds: selectedClipId ? [selectedClipId] : [] },
    }),
    [
      auditionModeState,
      addAnnotation,
      clearEditorialClipBinding,
      clipMediaMetadataByClipId,
      dispatch,
      bindEditorialClip,
      editorialClipBindingByClipId,
      editorialClipBindings,
      lanes,
      sonics,
      mediaFamilies,
      mediaMembers,
      recordClipMediaMetadata,
      narrativeSonicsReadModel,
      selectedClipId,
      setAuditionMode,
      state,
      updateAnnotation,
    ],
  );

  return <OmniCineTimelineContext.Provider value={value}>{children}</OmniCineTimelineContext.Provider>;
}

export function useOmniCineTimeline() {
  const ctx = useContext(OmniCineTimelineContext);
  if (!ctx) throw new Error("useOmniCineTimeline must be used within OmniCineTimelineProvider");
  return ctx;
}
